import { createSelector } from "@reduxjs/toolkit";
import {
  buildRoomSectionViewsFromMcBimItems,
  getRoomAxes,
  getRoomSectionViewDirs,
} from "@multiconsult-group/multiplacer-core";
import type {
  IRoomSectionDirection,
  IRoomSectionPolygon,
  IRoomAxes,
} from "@multiconsult-group/multiplacer-core";
import type {
  IPixPolygon,
  RoomSectionRenderableItem,
  RoomSectionRenderItem,
} from "@multiconsult-group/multiplacer-ui";
import { selectHttpActiveFullMcbimSpace } from "../McBim/mcbimSelectors";
import { selectHttpMcBimItemsInSelectedRoom } from "../RoomSideBar/singleRoomSelectors";
import { IMcBimItem } from "@multiconsult-group/multiplacer-core";
import type { IRoomSectionDirectionPolygons } from "../../../packages/shared/src/roomSectionViews/buildRoomSectionViewsFromMcBimItems";
import { buildRoomSectionDirectionPolygons } from "../../../packages/shared/src/roomSectionViews/buildRoomSectionViewsFromMcBimItems";
import { RoomSideBarTabs } from "@multiconsult-group/multiplacer-core";
import {
  selectClickedMcBimItemId,
  selectSectionCenterCoordinate,
  selectSelectedSection,
} from "../ViewerRedux/viewerSlice";
import { getMcBimItemDisplayColor } from "../DesignTab/transformMCBimItemsToPolygons";
import type { IMcBimItemColorMode } from "../DesignTab/designSlice";
import { selectMmiPath } from "../globalSelectors";
import { getDeeplyNestedValue } from "@multiconsult-group/multiplacer-core";
import { selectRoomSectionVisible2dItemIdentitySet } from "./roomSectionVisible2dSelectors";
import { selectRoomSectionPerimeterItems } from "./roomSectionPerimeterSelectors";

type ICoordinate2D = [number, number];
const MIN_ROOM_SECTION_CENTER_BOX_HALF_SIZE = 0.35;
const MAX_ROOM_SECTION_CENTER_BOX_HALF_SIZE = 0.8;
const ROOM_SECTION_CENTER_HANDLE_SEGMENTS = 24;

export interface IBuildRoomSectionViewsSelectorArgs {
  roomItems: IMcBimItem[];
  mcbimSpace: IMcBimItem;
  sectionCoordinate: ICoordinate2D;
}

export interface IRoomSectionPixPolygons {
  sideA: IPixPolygon[];
  sideB: IPixPolygon[];
  sideC: IPixPolygon[];
  sideD: IPixPolygon[];
  isometric: IPixPolygon[];
}

export type IIsoViewerRenderItem = RoomSectionRenderItem<RoomSectionRenderableItem>;

const selectRoomTab = (state: { design: { selectedRoomTab: RoomSideBarTabs | undefined } }) =>
  state.design.selectedRoomTab;
const selectMcbimItemColorMode = (state: { design: { mcbimItemColorMode: IMcBimItemColorMode } }) =>
  state.design.mcbimItemColorMode;

const getRoomSectionItemOpacity = (item: RoomSectionRenderableItem): number => {
  const instanceClass = item.instanceClass?.toLowerCase();
  if (instanceClass === "ifcwall" || instanceClass === "ifccurtainwall") {
    return 0.28;
  }

  return 1;
};

export const getRoomSectionCoordinate = (
  polygon: number[][] | null | undefined
): ICoordinate2D | null => {
  if (!polygon || polygon.length < 4) {
    return null;
  }

  const xs = polygon.map((point) => point[0]);
  const ys = polygon.map((point) => point[1]);
  return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
};

export const selectRoomAxes = createSelector(
  [selectHttpActiveFullMcbimSpace],
  (mcbimSpace): IRoomAxes | null => {
    if (!mcbimSpace) return null;
    return getRoomAxes(mcbimSpace);
  }
);

export interface IRoomSectionCameraDirs {
  sideA: { x: number; y: number; z: number };
  sideB: { x: number; y: number; z: number };
  sideC: { x: number; y: number; z: number };
  sideD: { x: number; y: number; z: number };
  isometric: { x: number; y: number; z: number };
}

/** Camera directions derived from the same source as the arrow polygons — guaranteed to stay in sync. */
export const selectRoomSectionCameraDirs = createSelector(
  [selectHttpActiveFullMcbimSpace],
  (mcbimSpace): IRoomSectionCameraDirs | null => {
    if (!mcbimSpace) return null;
    const viewDirs = getRoomSectionViewDirs(mcbimSpace);
    return {
      sideA: { x: viewDirs.sideA[0], y: viewDirs.sideA[1], z: 0 },
      sideB: { x: viewDirs.sideB[0], y: viewDirs.sideB[1], z: 0 },
      sideC: { x: viewDirs.sideC[0], y: viewDirs.sideC[1], z: 0 },
      sideD: { x: viewDirs.sideD[0], y: viewDirs.sideD[1], z: 0 },
      isometric: { x: 1, y: 1, z: 1 },
    };
  }
);

export const selectBuildRoomSectionViewsArgs = createSelector(
  [
    selectHttpMcBimItemsInSelectedRoom,
    selectHttpActiveFullMcbimSpace,
    selectSectionCenterCoordinate,
  ],
  (roomItems, mcbimSpace, sectionCenterCoordinate): IBuildRoomSectionViewsSelectorArgs | null => {
    if (!roomItems || !mcbimSpace) {
      return null;
    }

    if (!sectionCenterCoordinate) {
      return null;
    }

    return {
      roomItems,
      mcbimSpace,
      sectionCoordinate: sectionCenterCoordinate,
    };
  }
);

export const selectRoomSectionViews = createSelector([selectBuildRoomSectionViewsArgs], (args) => {
  if (!args) {
    return null;
  }

  return buildRoomSectionViewsFromMcBimItems(args);
});

export const selectIsoViewerRenderItems = createSelector(
  [
    selectHttpMcBimItemsInSelectedRoom,
    selectRoomSectionPerimeterItems,
    selectMcbimItemColorMode,
    selectMmiPath,
    selectClickedMcBimItemId,
    selectRoomSectionVisible2dItemIdentitySet,
  ],
  (
    roomItems,
    perimeterItems,
    mcbimItemColorMode,
    mmiPath,
    clickedMcBimItemId,
    visible2dItemIdentitySet
  ): IIsoViewerRenderItem[] =>
    [...(roomItems ?? []), ...(perimeterItems ?? [])]
      .filter((item, index, items) => {
        const identity = item._id != null ? String(item._id) : item.globalId;
        if (!identity) {
          return true;
        }

        return (
          items.findIndex((candidate) => {
            const candidateIdentity =
              candidate._id != null ? String(candidate._id) : candidate.globalId;
            return candidateIdentity === identity;
          }) === index
        );
      })
      .filter((item) => {
        if (!visible2dItemIdentitySet) {
          return true;
        }

        const idMatches =
          item._id != null && visible2dItemIdentitySet.has(String(item._id));
        const globalIdMatches =
          item.globalId != null && visible2dItemIdentitySet.has(item.globalId);

        return idMatches || globalIdMatches;
      })
      .map((item) => {
      const color =
        getMcBimItemDisplayColor({
          mcbimItemColorMode,
          item: item as Parameters<typeof getMcBimItemDisplayColor>[0]["item"],
          mmiPath,
        }) ?? "#74c0fc";

      return {
        item,
        color,
        opacity: getRoomSectionItemOpacity(item),
        outline:
          clickedMcBimItemId != null && String(item._id) === clickedMcBimItemId
            ? { color: "#ff0000", width: 3 }
            : undefined,
      };
    })
);

/** Base fill color in RGB (116, 192, 252) = #74c0fc */
const BASE_FILL_RGB: [number, number, number] = [116, 192, 252];

const shadeColor = (shade: number): string => {
  const [r, g, b] = BASE_FILL_RGB.map((c) => Math.round(c * shade));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

const buildCirclePolygon = (
  [centerX, centerY]: ICoordinate2D,
  radius: number,
  segments = ROOM_SECTION_CENTER_HANDLE_SEGMENTS
): number[][] => {
  const points = Array.from({ length: segments }, (_, index) => {
    const angle = (Math.PI * 2 * index) / segments;
    return [centerX + Math.cos(angle) * radius, centerY + Math.sin(angle) * radius];
  });

  return [...points, points[0]];
};

const convertSectionToPixPolygon = (
  mcBimItem: IMcBimItem,
  sectionPolygon: IRoomSectionPolygon,
  polygonIndex: number
): IPixPolygon => ({
  id: `section-${mcBimItem.globalId}-${polygonIndex}`,
  entityId: String(mcBimItem._id ?? mcBimItem.globalId),
  points: sectionPolygon.polygon,
  fillColor: shadeColor(sectionPolygon.shade),
  strokeColor: "#1971c2",
  strokeWidth: "0.2",
  opacity: 1.0,
  text: {
    values: [mcBimItem.name],
    color: "#0b2239",
    fontSize: "1",
    show: false,
  },
  tooltip: [mcBimItem.name, mcBimItem.instanceClass].filter(Boolean).join("\n"),
  isMoveable: false,
  isDragSelectable: false,
  isNotClickable: false,
});

export const selectRoomSectionPixPolygons = createSelector(
  [selectRoomSectionViews],
  (sectionViews): IRoomSectionPixPolygons | null => {
    if (!sectionViews) {
      return null;
    }

    const buildFloor = (direction: keyof typeof sectionViews): IPixPolygon[] => {
      const floor = sectionViews[direction]?.floorPolygon;
      if (!floor) return [];
      return [
        {
          id: `floor-${direction}`,
          entityId: `floor-${direction}`,
          points: floor,
          fillColor: "#adb5bd",
          strokeColor: "#868e96",
          strokeWidth: "0.2",
          opacity: 1.0,
          text: { values: [], color: "#000", fontSize: "1", show: false },
          tooltip: "",
          isMoveable: false,
          isDragSelectable: false,
          isNotClickable: true,
        },
      ];
    };

    const convertSections = (direction: keyof typeof sectionViews): IPixPolygon[] => [
      ...buildFloor(direction),
      ...(sectionViews[direction]?.sections ?? []).flatMap((section) =>
        section.sectionPolygons.map((sectionPolygon, polygonIndex) =>
          convertSectionToPixPolygon(section.mcBimItem, sectionPolygon, polygonIndex)
        )
      ),
    ];

    return {
      sideA: convertSections("sideA"),
      sideB: convertSections("sideB"),
      sideC: convertSections("sideC"),
      sideD: convertSections("sideD"),
      isometric: convertSections("isometric"),
    };
  }
);

export const selectRoomSectionDirectionPixPolygons = createSelector(
  [
    selectHttpActiveFullMcbimSpace,
    selectRoomTab,
    selectSelectedSection,
    selectSectionCenterCoordinate,
  ],
  (
    mcbimSpace,
    selectedRoomTab,
    selectedSection,
    sectionCenterCoordinate
  ): IRoomSectionPixPolygons | null => {
    if (selectedRoomTab !== RoomSideBarTabs.ROOM_SECTION_VIEWS) {
      return null;
    }

    if (!mcbimSpace) {
      return null;
    }

    if (!sectionCenterCoordinate) {
      return null;
    }

    const polygons: IRoomSectionDirectionPolygons = buildRoomSectionDirectionPolygons(
      sectionCenterCoordinate,
      mcbimSpace
    );

    const toPixPolygon = (direction: IRoomSectionDirection): IPixPolygon => ({
      id: `room-section-direction-${direction}`,
      entityId: `room-section-direction-${direction}`,
      points: polygons[direction],
      fillColor: "#228be6",
      strokeColor: "#1c7ed6",
      strokeWidth: direction === selectedSection ? "0.2" : "0",
      opacity: direction === selectedSection ? 1.0 : 0.0,
      text: {
        values: [direction === "isometric" ? "I" : direction.replace("side", "")],
        color: "#000000",
        fontSize: "2.2",
        show: true,
      },
      tooltip: direction,
      isMoveable: false,
      isDragSelectable: false,
      isNotClickable: false,
    });

    return {
      sideA: [toPixPolygon("sideA")],
      sideB: [toPixPolygon("sideB")],
      sideC: [toPixPolygon("sideC")],
      sideD: [toPixPolygon("sideD")],
      isometric: [toPixPolygon("isometric")],
    };
  }
);

export const selectRoomSectionCenterHandlePolygon = createSelector(
  [selectRoomTab, selectSectionCenterCoordinate, selectHttpActiveFullMcbimSpace],
  (selectedRoomTab, sectionCenterCoordinate, mcbimSpace): IPixPolygon | null => {
    if (selectedRoomTab !== RoomSideBarTabs.ROOM_SECTION_VIEWS) {
      return null;
    }

    if (!sectionCenterCoordinate) {
      return null;
    }

    const [centerX, centerY] = sectionCenterCoordinate;
    const bbox2d = mcbimSpace?.geometry?.bbox2d;
    const halfSize = (() => {
      if (!bbox2d || bbox2d.length < 2) {
        return MIN_ROOM_SECTION_CENTER_BOX_HALF_SIZE;
      }

      const xs = bbox2d.map((point) => point[0]);
      const ys = bbox2d.map((point) => point[1]);
      const width = Math.max(...xs) - Math.min(...xs);
      const height = Math.max(...ys) - Math.min(...ys);
      const proposedHalfSize = Math.min(width, height) * 0.12;

      return Math.max(
        MIN_ROOM_SECTION_CENTER_BOX_HALF_SIZE,
        Math.min(MAX_ROOM_SECTION_CENTER_BOX_HALF_SIZE, proposedHalfSize)
      );
    })();

    return {
      id: "room-section-center-handle",
      entityId: "room-section-center-handle",
      points: buildCirclePolygon([centerX, centerY], halfSize),
      fillColor: "#74c0fc",
      strokeColor: "#1c7ed6",
      strokeWidth: "0.15",
      opacity: 0.35,
      text: {
        values: [],
        color: "#0b2239",
        fontSize: "1",
        show: false,
      },
      tooltip: "Flytt seksjonspiler",
      isMoveable: true,
      isDragSelectable: false,
      isNotClickable: false,
    };
  }
);
